/**
 * S-73 海域図（視認・漁具）。担当: G4（F-RPT-04）
 * kintone 版 `portal/lookFishOceanAreaMap/*` の再現：視認／漁具・期間・月・事業実施者で検索し、
 * 海域図（視認＝ピンク、漁具＝緑）と件数／隻数（漁具は個数）、一覧を表示する。
 * 海域図の数字は、視認＝隻数の合計、漁具＝件数（kintone 検証指摘 R08.10.16）。
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Binoculars, Search } from 'lucide-react';
import { Button, Checkbox, DataTable, DateField, EmptyState, ErrorState, FormField, PageHeader, Panel, RadioGroup, type Column } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { BusinessOperatorSelect } from '@/features/masters';
import { SeaAreaMap, SeaMapLegend } from '@/features/portal/components/SeaAreaMap';
import { formatNumber } from '@/lib/format';
import { parseIdList, useFindings, type FindingKind, type FindingRowDto, type FindingsFilter } from './api';
import './sea-map.css';

const KINDS: { value: FindingKind; label: string }[] = [
  { value: '視認', label: '視認（外国漁船）' },
  { value: '漁具', label: '漁具（漁場調査等）' },
];
const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

/** 視認月日（MM月DD日） */
const monthDay = (d: string) => `${d.slice(5, 7)}月${d.slice(8, 10)}日`;
const yn = (v: boolean | null) => (v == null ? '' : v ? '有' : '無');

function useFindingParams(defaults: { from: string; to: string }) {
  const [params, setParams] = useSearchParams();
  const filter: FindingsFilter = useMemo(
    () => ({
      kind: params.get('kind') === '漁具' ? '漁具' : '視認',
      from: params.get('from') || defaults.from,
      to: params.get('to') || defaults.to,
      months: parseIdList(params.get('months')).filter((m) => m >= 1 && m <= 12),
      businessOperatorIds: parseIdList(params.get('ops')),
    }),
    [params, defaults.from, defaults.to],
  );
  const commit = (f: FindingsFilter) => {
    const p = new URLSearchParams();
    p.set('kind', f.kind);
    p.set('from', f.from);
    p.set('to', f.to);
    if (f.months.length) p.set('months', f.months.join(','));
    if (f.businessOperatorIds.length) p.set('ops', f.businessOperatorIds.join(','));
    setParams(p, { replace: true });
  };
  return { filter, commit };
}

export function SightingsMapPage() {
  const user = useCurrentUser();
  const { year, fiscalYear, yearPath } = useFiscalYear();
  const { filter, commit } = useFindingParams({ from: fiscalYear.startDate, to: fiscalYear.endDate });
  const [draft, setDraft] = useState(filter);
  const [periodError, setPeriodError] = useState<string | null>(null);
  const { data, error, isFetching, refetch } = useFindings(year, filter);
  const isLook = filter.kind === '視認';

  const onSearch = () => {
    if (draft.from && draft.to && draft.from > draft.to) {
      setPeriodError('期間開始日は期間終了日より前の日付を指定してください');
      return;
    }
    setPeriodError(null);
    commit({ ...draft, from: draft.from || fiscalYear.startDate, to: draft.to || fiscalYear.endDate });
  };
  const toggleMonth = (m: number, on: boolean) =>
    setDraft((d) => ({ ...d, months: on ? [...d.months, m] : d.months.filter((x) => x !== m) }));

  const reportLink = (r: FindingRowDto) =>
    r.voyageReportId ? (
      <Link className="bs-btn bs-btn--sm" to={yearPath(`voyage-reports/${r.voyageReportId}`)}>
        速報
      </Link>
    ) : r.fixedTimeReportId ? (
      <Link className="bs-btn bs-btn--sm" to={yearPath(`fixed-reports/${r.fixedTimeReportId}`)}>
        定時
      </Link>
    ) : null;

  const common: Column<FindingRowDto>[] = [
    { key: 'no', header: 'No.', width: 52, render: (_r, i) => <span className="bs-num">{i + 1}</span> },
    { key: 'date', header: isLook ? '視認月日' : '発見月日', sortable: true, width: 92, render: (r) => <span className="bs-num">{monthDay(r.date)}</span> },
    { key: 'time', header: '時刻', width: 64, render: (r) => <span className="bs-num">{r.time ?? ''}</span> },
    { key: 'monitoringArea', header: '監視作業水域', width: 120 },
    { key: 'zoneNo', header: '農林漁区番号', numeric: true, sortable: true, width: 86 },
    { key: 'latLon', header: '緯度・経度', width: 170, render: (r) => <span className="bs-num">{r.latLon}</span> },
  ];
  const lookCols: Column<FindingRowDto>[] = [
    { key: 'activity', header: '操業・航海・漂泊', width: 90 },
    { key: 'heading', header: '進行方向等', width: 90 },
    { key: 'nationality', header: '国籍', width: 64 },
    { key: 'foreignVesselName', header: '船名', width: 100 },
    { key: 'foreignRegistrationNo', header: '登録番号', width: 100 },
    { key: 'approvalNo', header: '承認番号', width: 90 },
    { key: 'fishingType', header: '漁業種類', width: 84 },
    { key: 'vesselCount', header: '隻数', numeric: true, width: 60 },
    { key: 'isReported', header: '通報', width: 56, align: 'center', render: (r) => yn(r.isReported) },
  ];
  const gearCols: Column<FindingRowDto>[] = [
    { key: 'gearType', header: '漁具種類', width: 110 },
    { key: 'gearCount', header: '個数', width: 60, align: 'right' },
    { key: 'mmsi', header: 'AIS/MMSI', width: 100 },
    { key: 'nationality', header: '国籍', width: 64 },
    { key: 'gearFeatures', header: '漁具の特徴', width: 140 },
  ];
  const tail: Column<FindingRowDto>[] = [
    { key: 'hasPhoto', header: '写真', width: 56, align: 'center', render: (r) => yn(r.hasPhoto) },
    { key: 'specialNotes', header: '特記事項', width: 140 },
    { key: 'businessOperatorName', header: '報告：事業実施者', width: 110 },
    { key: 'vesselName', header: '報告：船名', width: 100 },
    { key: 'tonnage', header: 'ﾄﾝ数', numeric: true, width: 64 },
    { key: 'ownerName', header: '所有者', width: 90 },
    { key: 'captainName', header: '船長', width: 90 },
    { key: 'link', header: '', width: 72, render: reportLink },
  ];
  const columns = [...common, ...(isLook ? lookCols : gearCols), ...tail];

  return (
    <>
      <PageHeader
        eyebrow="海域情報"
        title="海域図（視認・漁具）"
        description="外国漁船の視認状況報告・漁具の発見情報を農林漁区番号ごとに集計して海域図に表示します。"
      />
      <div className="bs-seamap-page">
        <Panel title="検索条件" className="bs-seamap-page__cond">
          <div className="bs-seamap-cond">
            <FormField label="条件選択">
              <RadioGroup name="kind" value={draft.kind} onChange={(v) => setDraft({ ...draft, kind: v })} options={KINDS} direction="column" />
            </FormField>
            <FormField label="期間" required error={periodError ?? undefined}>
              <div className="bs-seamap-cond__period">
                <DateField aria-label="期間開始" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
                <span>〜</span>
                <DateField aria-label="期間終了" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
              </div>
            </FormField>
            <FormField label="月" hint="チェックした月のみ表示（未選択は期間内すべて）">
              <div className="bs-seamap-cond__months" role="group" aria-label="月">
                {MONTHS.map((m) => (
                  <Checkbox key={m} label={`${m}月`} checked={draft.months.includes(m)} onChange={(e) => toggleMonth(m, e.target.checked)} />
                ))}
              </div>
            </FormField>
            <FormField label="事業実施者" required={user.organizationType === '漁協'} hint={user.organizationType === '漁協' ? '自組織のみ表示されます' : '未選択はすべての事業実施者'}>
              <BusinessOperatorSelect multiple fiscalYear={year} value={draft.businessOperatorIds} onChange={(v) => setDraft((d) => ({ ...d, businessOperatorIds: v }))} />
            </FormField>
            <div className="bs-seamap-cond__actions">
              <Button variant="primary" icon={<Search size={16} />} loading={isFetching} onClick={onSearch}>
                検索
              </Button>
            </div>
          </div>
        </Panel>
        <Panel
          title="海域図"
          className="bs-seamap-page__map"
          actions={
            data && (
              <span className="bs-seamap-page__stat">
                件数 <strong className="bs-num">{formatNumber(data.totalCount)}</strong> 件、{isLook ? '隻数' : '個数'}{' '}
                <strong className="bs-num">{formatNumber(data.totalVesselCount)}</strong> {isLook ? '隻' : '個'}
              </span>
            )
          }
        >
          {error ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : (
            <SeaAreaMap
              counts={data?.zoneCounts ?? []}
              tone={isLook ? 'pink' : 'green'}
              unit={isLook ? '隻' : '件'}
              legend={
                <>
                  <SeaMapLegend tone={isLook ? 'pink' : 'green'}>{isLook ? '視認した海域' : '漁具を発見した海域'}</SeaMapLegend>
                  {/* kintone 検証指摘：視認は数字の合計＝隻数の合計、漁具は個数が任意入力のため件数 */}
                  <span>{isLook ? '数字：隻数' : '数字：件数'}（農林漁区番号は手入力を優先、無い場合は緯度経度からの機械算出）</span>
                </>
              }
            />
          )}
        </Panel>
      </div>
      <section className="bs-seamap-page__result" aria-label="検索結果一覧">
        <h2 className="bs-seamap-page__h2">検索結果一覧（{filter.kind}）</h2>
        <DataTable
          caption={`検索結果一覧（${filter.kind}）`}
          columns={columns}
          rows={data?.rows ?? []}
          rowKey={(r) => `${r.kind}-${r.id}`}
          loading={!data && isFetching}
          pagination={{ pageSize: 50 }}
          dense
          empty={<EmptyState compact icon={<Binoculars />} title="該当するデータは存在しませんでした。" />}
        />
      </section>
    </>
  );
}
